import { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, Dumbbell, HeartPulse, Loader2, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { getEmailApiBaseUrl } from '../../utils/emailApi';
import LoadingSpinner from '../common/LoadingSpinner';

function parseCanManageOwnWorkouts(value) {
    if (value === undefined || value === null) return true;
    return Number(value) === 1;
}

function parseHealthDataConsent(value) {
    if (value === undefined || value === null) return false;
    return Number(value) === 1;
}

export default function MemberConsentsTab({ member, canModify, onMemberUpdated }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    const emailApi = getEmailApiBaseUrl();
    const isMember = member?.role_in_gym === 'member' || member?.membership_role === 'member';

    const [loading, setLoading] = useState(true);
    const [savingMarketing, setSavingMarketing] = useState(false);
    const [savingWorkouts, setSavingWorkouts] = useState(false);
    const [savingHealthData, setSavingHealthData] = useState(false);
    const [marketingOptIn, setMarketingOptIn] = useState(false);
    const [canManageOwnWorkouts, setCanManageOwnWorkouts] = useState(true);
    const [healthDataConsent, setHealthDataConsent] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const api = getAuthenticatedApi();
            const res = await api.get(
                `gym_email_tags.php?action=member_tags&user_id=${member.id}`,
                { baseURL: emailApi },
            );
            setMarketingOptIn(!!res.data?.marketing_opt_in);
            setCanManageOwnWorkouts(parseCanManageOwnWorkouts(member.can_manage_own_workouts));
            setHealthDataConsent(parseHealthDataConsent(member.health_data_consent));
        } catch (err) {
            toast.error(err.response?.data?.error || t('gym.membersManager.consents.loadError'));
        } finally {
            setLoading(false);
        }
    }, [getAuthenticatedApi, emailApi, member.id, member.can_manage_own_workouts, member.health_data_consent, t]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleMarketingChange = async (checked) => {
        if (!canModify) return;
        setSavingMarketing(true);
        try {
            const api = getAuthenticatedApi();
            await api.post(
                'gym_email_tags.php?action=set_marketing_opt_in',
                { user_id: member.id, marketing_opt_in: checked },
                { baseURL: emailApi },
            );
            setMarketingOptIn(checked);
            toast.success(
                checked
                    ? t('gym.membersManager.consents.marketingEnabled')
                    : t('gym.membersManager.consents.marketingDisabled'),
            );
        } catch (err) {
            toast.error(err.response?.data?.error || t('gym.membersManager.consents.marketingSaveError'));
        } finally {
            setSavingMarketing(false);
        }
    };

    const handleHealthDataChange = async (checked) => {
        if (!canModify || !isMember) return;
        setSavingHealthData(true);
        try {
            const api = getAuthenticatedApi();
            await api.put(`/gym_members.php?user_id=${member.id}`, {
                health_data_consent: checked ? 1 : 0,
            });
            setHealthDataConsent(checked);
            onMemberUpdated?.({
                id: member.id,
                health_data_consent: checked ? 1 : 0,
            });
            toast.success(
                checked
                    ? t('gym.membersManager.consents.healthDataEnabled')
                    : t('gym.membersManager.consents.healthDataDisabled'),
            );
        } catch (err) {
            toast.error(err.response?.data?.error || t('gym.membersManager.consents.healthDataSaveError'));
        } finally {
            setSavingHealthData(false);
        }
    };

    const handleWorkoutsChange = async (checked) => {
        if (!canModify || !isMember) return;
        setSavingWorkouts(true);
        try {
            const api = getAuthenticatedApi();
            await api.put(`/gym_members.php?user_id=${member.id}`, {
                can_manage_own_workouts: checked ? 1 : 0,
            });
            setCanManageOwnWorkouts(checked);
            onMemberUpdated?.({
                id: member.id,
                can_manage_own_workouts: checked ? 1 : 0,
            });
            toast.success(t('gym.membersManager.consents.workoutsSaveSuccess'));
        } catch (err) {
            toast.error(err.response?.data?.error || t('gym.membersManager.consents.workoutsSaveError'));
        } finally {
            setSavingWorkouts(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <LoadingSpinner />
            </div>
        );
    }

    const hasEmail = !!(member.email && String(member.email).trim());

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('gym.membersManager.consents.title')}
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-white/65">
                    {t('gym.membersManager.consents.subtitle')}
                </p>
            </div>

            {isMember && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-gray-900/80">
                    <div className="mb-3 flex items-center gap-2">
                        <HeartPulse className="h-5 w-5 text-rose-600 dark:text-rose-400" aria-hidden />
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {t('gym.membersManager.consents.healthDataTitle')}
                        </h4>
                    </div>
                    <label className={`flex items-start gap-3 ${canModify ? 'cursor-pointer' : 'cursor-default'}`}>
                        <input
                            type="checkbox"
                            checked={healthDataConsent}
                            disabled={!canModify || savingHealthData}
                            onChange={(e) => handleHealthDataChange(e.target.checked)}
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-rose-600 focus:ring-rose-500 disabled:opacity-50 dark:border-white/30 dark:bg-gray-800"
                        />
                        <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-gray-900 dark:text-white">
                                {t('gym.membersManager.consents.healthDataLabel')}
                            </span>
                            <span className="mt-1 block text-xs text-gray-500 dark:text-white/60">
                                {t('gym.membersManager.consents.healthDataHint')}
                            </span>
                        </span>
                        {savingHealthData && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" />}
                    </label>
                </div>
            )}

            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-gray-900/80">
                <div className="mb-3 flex items-center gap-2">
                    <Mail className="h-5 w-5 text-indigo-600 dark:text-indigo-400" aria-hidden />
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                        {t('gym.membersManager.consents.marketingTitle')}
                    </h4>
                </div>
                <label className={`flex items-start gap-3 ${canModify ? 'cursor-pointer' : 'cursor-default'}`}>
                    <input
                        type="checkbox"
                        checked={marketingOptIn}
                        disabled={!canModify || savingMarketing}
                        onChange={(e) => handleMarketingChange(e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 dark:border-white/30 dark:bg-gray-800"
                    />
                    <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-gray-900 dark:text-white">
                            {t('gym.membersManager.consents.marketingLabel')}
                        </span>
                        <span className="mt-1 block text-xs text-gray-500 dark:text-white/60">
                            {t('gym.membersManager.consents.marketingHint')}
                        </span>
                    </span>
                    {savingMarketing && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" />}
                </label>
                {!hasEmail && (
                    <p className="mt-3 text-xs text-amber-700 dark:text-amber-200">
                        {t('gym.membersManager.consents.noEmail')}
                    </p>
                )}
            </div>

            {isMember && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-gray-900/80">
                    <div className="mb-3 flex items-center gap-2">
                        <Dumbbell className="h-5 w-5 text-indigo-600 dark:text-indigo-400" aria-hidden />
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {t('gym.membersManager.consents.workoutsTitle')}
                        </h4>
                    </div>
                    <label className={`flex items-start gap-3 ${canModify ? 'cursor-pointer' : 'cursor-default'}`}>
                        <input
                            type="checkbox"
                            checked={canManageOwnWorkouts}
                            disabled={!canModify || savingWorkouts}
                            onChange={(e) => handleWorkoutsChange(e.target.checked)}
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 dark:border-white/30 dark:bg-gray-800"
                        />
                        <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-gray-900 dark:text-white">
                                {t('gym.membersManager.consents.workoutsLabel')}
                            </span>
                            <span className="mt-1 block text-xs text-gray-500 dark:text-white/60">
                                {t('gym.membersManager.consents.workoutsHint')}
                            </span>
                        </span>
                        {savingWorkouts && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" />}
                    </label>
                </div>
            )}

            <div className="flex items-start gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50/80 p-4 text-sm text-gray-600 dark:border-white/10 dark:bg-gray-900/40 dark:text-white/70">
                <ClipboardCheck className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" aria-hidden />
                <p>{t('gym.membersManager.consents.infoNote')}</p>
            </div>
        </div>
    );
}
